import { type ComponentPropsWithoutRef, type ReactNode } from "react"

import { useCachedImageUrl } from "@/hooks/useCachedImageUrl"

type CachedImageProps = Omit<ComponentPropsWithoutRef<"img">, "src"> & {
  src?: string | null
  fallback?: ReactNode
}

/**
 * Image component for mobile app surfaces.
 *
 * Remote URLs that match an offline cache policy are resolved through
 * `useCachedImageUrl` before rendering, so Capacitor builds can use the
 * app-managed cache path instead of raw WebView image loading. URLs that do
 * not match a cache policy, such as blob URLs from locally stored photos or
 * statically imported assets, pass through unchanged and are not copied into
 * another cache.
 *
 * `fallback` is rendered while a cacheable URL is resolving, or when `src` is
 * nullish. Filesystem-backed media policies resolve to files in Capacitor's
 * OS-managed `Directory.Cache` in native builds and pass through in web builds.
 */
export function CachedImage({
  src,
  fallback = null,
  onError,
  ...props
}: CachedImageProps) {
  const { src: resolvedSrc, invalidate } = useCachedImageUrl(src)
  if (!resolvedSrc) return fallback

  return (
    <img
      {...props}
      src={resolvedSrc}
      onError={(event) => {
        void invalidate()
        onError?.(event)
      }}
    />
  )
}
